import type { ZodTypeAny } from "zod";
import { z as zod } from "zod";
import { makeZodI18nMap } from "zod-i18n-map";
import { isAfter } from "date-fns";
import type { ParseKeys } from "i18next";
import i18n from "i18next";

import { isPhoneValid } from "@/utils/phoneNumber";
import type { IFile, IFileObject, IOption } from "@/interfaces";

const dropdown = (optional = false) =>
  zod.custom<IOption | undefined | null>().refine(
    (val) => {
      return (
        optional ||
        (val?.key !== undefined &&
          val?.key !== null &&
          val?.key.toString().trim() !== "" &&
          val.value)
      );
    },
    { params: { i18n: "errors.invalid_type_received_undefined" } },
  );

const fileObject = (optional = false) =>
  zod.custom<IFileObject>().refine(
    (val) => {
      return (
        optional ||
        (val?.id !== undefined &&
          val?.id !== null &&
          val?.id.toString().trim() !== "")
      );
    },
    { params: { i18n: "file" } },
  );

const macAddress = () =>
  zod.string().refine(
    (val) => {
      // Regular expression for MAC address validation
      const macRegex = /^([\dA-Fa-f]{2}[:-]){5}([\dA-Fa-f]{2})$/;

      // Check if the provided value matches the MAC address format

      return macRegex.test(val);
    },
    {
      // Custom error message when validation fails
      params: { i18n: "errors.invalidMac" },
    },
  );

export const phone = (optional = false) =>
  zod.custom<{ code: string; number: string }>().superRefine((val, ctx) => {
    if (optional) {
      if (val?.number?.length > 0) {
        const isValid = isPhoneValid(val);

        if (!isValid) {
          ctx.addIssue({
            code: "custom",
            params: { i18n: "invalidPhoneNumber" },
          });
        }
      }
    } else if (!val?.number || !val.code) {
      ctx.addIssue({
        code: "custom",
        params: { i18n: "errors.invalid_type_received_undefined" },
      });
    } else {
      const isValid = isPhoneValid(val);

      if (!isValid) {
        ctx.addIssue({
          code: "custom",
          params: { i18n: "invalidPhoneNumber" },
        });
      }
    }
  });

const password = () =>
  zod
    .string()
    .min(8, {
      message: i18n.t("password.length", { ns: "zod" }),
    })
    .regex(/[a-z]/, {
      message: i18n.t("password.lowerCase", { ns: "zod" }),
    })
    .regex(/[A-Z]/, {
      message: i18n.t("password.upperCase", { ns: "zod" }),
    })
    .regex(/\d/, {
      message: i18n.t("password.number", { ns: "zod" }),
    });

// birthDate: z
//   .string()
//   .transform((d) => new Date(d))
//   .pipe(z.date().max(date))

export const minAge = () =>
  zod.string().superRefine((val, ctx) => {
    const date = new Date();

    date.setFullYear(date.getFullYear() - 18);
    const receivedDate = new Date(val);

    if (isAfter(receivedDate, date)) {
      ctx.addIssue({
        code: "custom",
        params: { i18n: "minAge" },
      });
    }
  });

const nationalId = () =>
  zod.custom<string>(
    (id) => {
      // First, ensure 'id' is a string
      if (typeof id !== "string") {
        return false;
      }

      // Iranian National ID should be exactly 10 digits
      if (!/^\d{10}$/.test(id)) {
        return false;
      }

      let sum = 0;

      for (let i = 0; i < 9; i++) {
        sum += Number.parseInt(id.charAt(i)) * (10 - i);
      }
      const remainder = sum % 11;

      return (
        (remainder < 2 && Number.parseInt(id.charAt(9)) === remainder) ||
        (remainder >= 2 && Number.parseInt(id.charAt(9)) === 11 - remainder)
      );
    },
    {
      params: { i18n: "invalidNationalId" },
    },
  );

const email = () =>
  zod.custom<string | undefined | null>(
    (email) => {
      // Ensure email is either undefined, null, or a non-empty string
      if (
        email === undefined ||
        email === null ||
        (typeof email === "string" && email.trim() === "")
      ) {
        return true;
      }

      // If email is a string, perform regex validation
      if (typeof email === "string") {
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        return pattern.test(email);
      }

      // If email is not a string, undefined, or null, it's invalid
      return false;
    },
    {
      params: { i18n: "invalidEmail" },
    },
  );

const postalCode = () =>
  zod.custom<string | undefined | null>(
    (code) => {
      // Allow the field to be optional: accept undefined, null, or empty string
      if (
        code === undefined ||
        code === null ||
        (typeof code === "string" && code.trim() === "")
      ) {
        return true;
      }

      // Ensure the input is a string before proceeding with further validation
      if (typeof code !== "string") {
        return false;
      }

      // Regex for Iranian postal code: exactly 10 digits
      const pattern = /^\d{10}$/;

      return pattern.test(code);
    },
    {
      params: { i18n: "invalidPostalCode" },
    },
  );

const file = (optional = false) =>
  zod.custom<IFile[]>().refine(
    (val) => {
      return optional || val.length > 0;
    },
    { params: { i18n: "file" } },
  );

export const passwordConfirmationErrorParam = { i18n: "password.match" };

zod.setErrorMap(makeZodI18nMap({ ns: ["zod", "form"] }));

export type IShapeOf<T> = Partial<Record<keyof T, ZodTypeAny>>;

export type IShapeOfField<T> = Partial<
  Record<keyof T & ParseKeys<"field">, ZodTypeAny>
>;

// eslint-disable-next-line id-length
export const z = {
  ...zod,
  dropdown,
  password,
  passwordConfirmationErrorParam,
  phone,
  minAge,
  nationalId,
  email,
  postalCode,
  macAddress,
  file,
  fileObject,
};

export { toFormikValidationSchema as toFormikSchema } from "zod-formik-adapter";
